import { useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useSacredAdmin } from "@/hooks/useSacredAdmin";
import { getEditorAuth } from "@/lib/sacred-library/sacred-library-api";
import {
  queryAcharya,
  loadAcharyaContext,
  fetchAcharyaSuggestions,
  logAcharyaError,
} from "@/lib/acharya/acharya-service";
import { fetchAcharyaDebug } from "@/lib/acharya/acharya-api";
import { AcharyaStructuredBlocks } from "./AcharyaStructuredBlocks";
import { Loader2, ArrowLeft } from "lucide-react";

export function AcharyaTest({ token: appToken, user: appUser }) {
  useSacredAdmin();
  const [question, setQuestion] = useState("What is Shiva tattva?");
  const [userId, setUserId] = useState(appUser?.id ?? "");
  const [email, setEmail] = useState(appUser?.email ?? "");
  const [result, setResult] = useState(null);
  const [debug, setDebug] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const token = appToken || (typeof localStorage !== "undefined" ? localStorage.getItem("vedaPath_token") : null);

  const runQuery = async () => {
    setLoading(true);
    setMessage("");
    try {
      const ctx = token ? await loadAcharyaContext(token) : null;
      const uid = userId.trim() || ctx?.profile?.id;
      const data = await queryAcharya(question.trim(), uid, token, ctx);
      setResult(data);
      setMessage("Query OK");
    } catch (e) {
      const friendly = await logAcharyaError(question, e, { userId, email });
      setMessage(friendly);
      setResult(null);
    }
    setLoading(false);
  };

  const runDebug = async () => {
    setLoading(true);
    try {
      const d = await fetchAcharyaDebug(email.trim() || undefined, question.trim());
      setDebug(d);
      setMessage("Debug loaded");
    } catch (e) {
      setMessage(e?.response?.data?.detail || "Debug failed (admin login)");
    }
    setLoading(false);
  };

  const loadSuggestions = async () => {
    try {
      setSuggestions(await fetchAcharyaSuggestions(token));
    } catch {
      setSuggestions([]);
    }
  };

  if (!getEditorAuth().token && !token) {
    return (
      <div className="min-h-screen py-24 px-4">
        <Link to="/auth" className="text-primary underline">
          Sign in
        </Link>{" "}
        to test Acharya (user token or sacred admin for debug).
      </div>
    );
  }

  return (
    <div className="min-h-screen py-24 px-4" data-testid="acharya-test-page">
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/guide">
              <ArrowLeft className="w-4 h-4 mr-1" /> Guide
            </Link>
          </Button>
          <h1 className="font-heading text-2xl">Acharya 3.0 Test</h1>
        </div>
        <p className="text-sm text-muted-foreground">
          Console:{" "}
          <code className="text-xs bg-muted px-1 rounded">
            testAcharya(&quot;{question}&quot;, &quot;{userId || email || "userId"}&quot;)
          </code>
        </p>

        <Card>
          <CardContent className="pt-6 space-y-4">
            <div>
              <Label>Question</Label>
              <Input value={question} onChange={(e) => setQuestion(e.target.value)} />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>User ID</Label>
                <Input value={userId} onChange={(e) => setUserId(e.target.value)} placeholder="UUID" />
              </div>
              <div>
                <Label>Email (debug)</Label>
                <Input value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={runQuery} disabled={loading}>
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Run query
              </Button>
              <Button variant="secondary" onClick={runDebug} disabled={loading}>
                Admin debug
              </Button>
              <Button variant="outline" onClick={loadSuggestions}>
                Load suggestions
              </Button>
            </div>
            {message && <p className="text-sm text-muted-foreground">{message}</p>}
          </CardContent>
        </Card>

        {suggestions.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Suggestions</CardTitle>
            </CardHeader>
            <CardContent className="text-xs space-y-1">
              {suggestions.map((s) => (
                <p key={s.id}>
                  [{s.source}] {s.question}
                </p>
              ))}
            </CardContent>
          </Card>
        )}

        {result && (
          <>
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Answer</CardTitle>
              </CardHeader>
              <CardContent className="text-sm whitespace-pre-wrap">{result.answer}</CardContent>
            </Card>
            <AcharyaStructuredBlocks data={result} />
          </>
        )}

        {debug && (
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Debug JSON</CardTitle>
            </CardHeader>
            <CardContent>
              <pre className="text-xs overflow-auto max-h-96 bg-muted p-3 rounded">
                {JSON.stringify(debug, null, 2)}
              </pre>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
